import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Star, MessageSquare, User, Send, Loader2, ThumbsUp, 
  CheckCircle, XCircle, Edit, Trash2, Search, Filter,
  BarChart3, TrendingUp, MapPin, Building2
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ rating, onRatingChange, readonly = false, size = "md" }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-5 w-5";

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onRatingChange && onRatingChange(star)}
          onMouseEnter={() => !readonly && setHoverRating(star)}
          onMouseLeave={() => !readonly && setHoverRating(0)}
          className={`${readonly ? "cursor-default" : "cursor-pointer hover:scale-110"} transition-transform`}
        >
          <Star
            className={`${sizeClass} ${
              star <= (hoverRating || rating)
                ? "fill-[#c9a227] text-[#c9a227]"
                : "fill-slate-200 text-slate-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function Feedback() {
  const [activeTab, setActiveTab] = useState("feedback"); // feedback, reviews, analytics
  const [reviews, setReviews] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchLogs, setSearchLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editReview, setEditReview] = useState(null);
  
  const [newFeedback, setNewFeedback] = useState({
    rating: 5,
    title: "",
    comment: ""
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const isGuest = user.role === "guest" || !user.id;

  useEffect(() => {
    if (activeTab === "feedback" || activeTab === "reviews") {
      fetchReviews();
    } else if (activeTab === "analytics" && isAdmin) {
      fetchAnalytics();
      fetchSearchLogs();
    }
  }, [activeTab]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const includeHidden = isAdmin ? "&include_hidden=true" : "";
      const response = await axios.get(`${API}/reviews?${includeHidden}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/search-logs/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    }
  };

  const fetchSearchLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/search-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch search logs:", error);
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    
    if (isGuest) {
      toast.error("Please login to submit feedback");
      return;
    }

    if (!newFeedback.comment.trim()) {
      toast.error("Please write your feedback");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/reviews`, {
        review_type: "platform_feedback",
        rating: newFeedback.rating,
        title: newFeedback.title || null,
        comment: newFeedback.comment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.moderation_status === "rejected") {
        toast.warning(response.data.message || "Your feedback is pending approval");
      } else {
        toast.success("Feedback submitted successfully!");
        fetchReviews();
      }
      
      setNewFeedback({ rating: 5, title: "", comment: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateReview = async () => {
    if (!editReview) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/reviews/${editReview.id}`, {
        moderation_status: editReview.moderation_status,
        is_visible: editReview.is_visible,
        admin_response: editReview.admin_response
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Review updated");
      setEditReview(null);
      fetchReviews();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const handleDeleteReview = async () => {
    if (!deleteId) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/reviews/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Review deleted");
      setDeleteId(null);
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const platformFeedback = reviews.filter(r => r.review_type === "platform_feedback");
  const propertyReviews = reviews.filter(r => r.review_type === "property_review");

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0f2744]" style={{ fontFamily: 'Playfair Display, serif' }}>
            {isAdmin ? "Reviews & Analytics" : "Feedback"}
          </h1>
          <p className="text-slate-600 mt-1">
            {isAdmin ? "Manage reviews, feedback, and view search analytics" : "Share your experience with CAPIN Realty"}
          </p>
        </div>

        {/* Tabs - Admin only sees all tabs */}
        {isAdmin && (
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === "feedback" ? "default" : "outline"}
              onClick={() => setActiveTab("feedback")}
              className={activeTab === "feedback" ? "bg-[#0f2744]" : ""}
            >
              <ThumbsUp className="h-4 w-4 mr-2" />
              Platform Feedback ({platformFeedback.length})
            </Button>
            <Button
              variant={activeTab === "reviews" ? "default" : "outline"}
              onClick={() => setActiveTab("reviews")}
              className={activeTab === "reviews" ? "bg-[#0f2744]" : ""}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Property Reviews ({propertyReviews.length})
            </Button>
            <Button
              variant={activeTab === "analytics" ? "default" : "outline"}
              onClick={() => setActiveTab("analytics")}
              className={activeTab === "analytics" ? "bg-[#0f2744]" : ""}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Search Analytics
            </Button>
          </div>
        )}

        {/* Platform Feedback Form - Non-admin users */}
        {!isAdmin && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold text-[#0f2744] mb-4">Share Your Feedback</h2>
            
            {isGuest ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800">Please login to submit feedback.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmitFeedback} className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-600">Your Rating</Label>
                  <div className="mt-1">
                    <StarRating
                      rating={newFeedback.rating}
                      onRatingChange={(rating) => setNewFeedback({ ...newFeedback, rating })}
                      size="lg"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="feedback-title">Title (Optional)</Label>
                  <Input
                    id="feedback-title"
                    placeholder="Summarize your feedback"
                    value={newFeedback.title}
                    onChange={(e) => setNewFeedback({ ...newFeedback, title: e.target.value })}
                    className="mt-1"
                    data-testid="feedback-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="feedback-comment">Your Feedback *</Label>
                  <Textarea
                    id="feedback-comment"
                    placeholder="Tell us about your experience with CAPIN Realty..."
                    value={newFeedback.comment}
                    onChange={(e) => setNewFeedback({ ...newFeedback, comment: e.target.value })}
                    className="mt-1 min-h-[120px]"
                    required
                    data-testid="feedback-comment-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#0f2744] hover:bg-[#1e3a5f]"
                  data-testid="submit-feedback-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        )}

        {/* Reviews List */}
        {(activeTab === "feedback" || activeTab === "reviews") && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-[#0f2744] to-[#1e3a5f]">
              <h3 className="text-lg font-semibold text-white">
                {activeTab === "feedback" ? "Platform Feedback" : "Property Reviews"}
              </h3>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#0f2744] mx-auto" />
                </div>
              ) : (activeTab === "feedback" ? platformFeedback : propertyReviews).length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No {activeTab === "feedback" ? "feedback" : "reviews"} yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(activeTab === "feedback" ? platformFeedback : propertyReviews).map((review) => (
                    <div
                      key={review.id}
                      className={`p-4 border rounded-lg ${
                        !review.is_visible ? "border-red-200 bg-red-50/50" : "border-slate-200"
                      }`}
                      data-testid={`review-item-${review.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0f2744] flex items-center justify-center">
                            <User className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-[#0f2744]">{review.user_name}</p>
                            <p className="text-xs text-slate-500">{formatDate(review.created_at)}</p>
                            {review.property_title && (
                              <p className="text-xs text-[#c9a227]">Property: {review.property_title}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StarRating rating={review.rating} readonly size="sm" />
                          {isAdmin && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              review.moderation_status === "approved" 
                                ? "bg-green-100 text-green-700"
                                : review.moderation_status === "rejected"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}>
                              {review.moderation_status}
                            </span>
                          )}
                        </div>
                      </div>

                      {review.title && (
                        <h5 className="font-semibold text-slate-800 mb-1">{review.title}</h5>
                      )}
                      <p className="text-sm text-slate-600">{review.comment}</p>

                      {review.moderation_reason && isAdmin && (
                        <p className="text-xs text-slate-500 mt-2 italic">
                          Moderation: {review.moderation_reason}
                        </p>
                      )}

                      {review.admin_response && (
                        <div className="mt-3 p-3 bg-[#f0f4f8] rounded-lg border-l-4 border-[#0f2744]">
                          <p className="text-xs text-slate-500 mb-1">Response from CAPIN Realty</p>
                          <p className="text-sm text-slate-700">{review.admin_response}</p>
                        </div>
                      )}

                      {/* Admin Actions */}
                      {isAdmin && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditReview({
                              ...review,
                              admin_response: review.admin_response || ""
                            })}
                            className="text-xs"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit / Respond
                          </Button>
                          {!review.is_visible && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditReview({
                                  ...review,
                                  moderation_status: "approved",
                                  is_visible: true
                                });
                                handleUpdateReview();
                              }}
                              className="text-xs text-green-600 border-green-200"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeleteId(review.id)}
                            className="text-xs text-red-600 border-red-200"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab - Admin Only */}
        {activeTab === "analytics" && isAdmin && (
          <div className="space-y-6">
            {/* Analytics Summary Cards */}
            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Search className="h-5 w-5 text-[#0f2744]" />
                    <h4 className="font-semibold text-slate-600">Total Searches</h4>
                  </div>
                  <p className="text-3xl font-bold text-[#0f2744]">{analytics.total_searches}</p>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="h-5 w-5 text-[#c9a227]" />
                    <h4 className="font-semibold text-slate-600">Guest Searches</h4>
                  </div>
                  <p className="text-3xl font-bold text-[#c9a227]">{analytics.guest_searches}</p>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold text-slate-600">User Searches</h4>
                  </div>
                  <p className="text-3xl font-bold text-green-600">{analytics.user_searches}</p>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold text-slate-600">Last 7 Days</h4>
                  </div>
                  <p className="text-3xl font-bold text-blue-600">{analytics.recent_7_days}</p>
                </div>
              </div>
            )}

            {/* Top Search Terms & Locations */}
            {analytics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Top Search Terms */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="font-semibold text-[#0f2744] mb-4 flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    Top Search Terms
                  </h4>
                  {analytics.top_search_terms.length === 0 ? (
                    <p className="text-sm text-slate-500">No search terms yet</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.top_search_terms.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{item.term}</span>
                          <span className="text-xs px-2 py-1 bg-[#f0f4f8] text-[#0f2744] rounded-full">
                            {item.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Top Property Types */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="font-semibold text-[#0f2744] mb-4 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Top Property Types
                  </h4>
                  {analytics.top_property_types.length === 0 ? (
                    <p className="text-sm text-slate-500">No data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.top_property_types.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600 capitalize">{item.type}</span>
                          <span className="text-xs px-2 py-1 bg-[#fffdf5] text-[#c9a227] rounded-full">
                            {item.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Top Locations */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="font-semibold text-[#0f2744] mb-4 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Top Searched Locations
                  </h4>
                  {analytics.top_locations.length === 0 ? (
                    <p className="text-sm text-slate-500">No locations yet</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.top_locations.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{item.location}</span>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                            {item.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent Search Logs */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-[#0f2744] to-[#1e3a5f]">
                <h3 className="text-lg font-semibold text-white">Recent Search Activity</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">User Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Search Term</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Filters Used</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Results</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {searchLogs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                          No search logs yet
                        </td>
                      </tr>
                    ) : (
                      searchLogs.map((log, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded ${
                              log.user_type === "guest" 
                                ? "bg-amber-100 text-amber-700"
                                : "bg-green-100 text-green-700"
                            }`}>
                              {log.user_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {log.search_term || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(log.filters || {}).map(([key, value]) => (
                                <span key={key} className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                                  {key}: {value}
                                </span>
                              ))}
                              {Object.keys(log.filters || {}).length === 0 && (
                                <span className="text-xs text-slate-400">None</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {log.results_count}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {formatDate(log.created_at)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Review Modal */}
      {editReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[#0f2744] mb-4">Edit Review</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Moderation Status</Label>
                <select
                  value={editReview.moderation_status}
                  onChange={(e) => setEditReview({ ...editReview, moderation_status: e.target.value })}
                  className="w-full mt-1 h-10 rounded-lg border border-slate-200 px-3"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <Label>Admin Response</Label>
                <Textarea
                  value={editReview.admin_response}
                  onChange={(e) => setEditReview({ ...editReview, admin_response: e.target.value })}
                  placeholder="Add a response to this review..."
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditReview(null)}>Cancel</Button>
                <Button onClick={handleUpdateReview} className="bg-[#0f2744]">Save Changes</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this review? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteReview} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
